import React from 'react';
import { Button, Form, Select, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Field, FieldProps, FastField } from 'formik';
import { get as _get } from 'lodash';
import { SelectProps } from 'antd/lib/select';
import type { CustomTagProps } from 'rc-select/lib/BaseSelect';
import { useTranslation } from 'react-i18next';
import AntTooltip from '../AntTooltip';

type FormMultiMultiSelectProps = {
  name: string;
  className?: string;
  values: { id: number | string; name: string }[];
  label?: string | React.ReactNode;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  fastField?: boolean;
  showTagTooltip?: boolean;
  getTagTooltip?: (id: number) => string;
} & SelectProps<any>;

const FormMultiMultiSelect: React.FC<FormMultiMultiSelectProps> = ({
  name,
  className,
  values,
  label,
  placeholder,
  required,
  disabled,
  loading,
  fastField,
  getTagTooltip,
  showTagTooltip,
  ...selectProps
}) => {
  const { t } = useTranslation();

  const valueList = React.useMemo(
    () =>
      values.map((value: { id: number | string; name: string }) => ({
        key: value.id,
        value: value.id,
        // We use the value of the innerHTML for the filterOptions - if we provide
        // anything else than a string here, the search would break.
        label: value.name,
      })),
    [values]
  );

  const tagRender = (props: CustomTagProps) => {
    const { label, closable, value, onClose } = props;
    const tooltip = getTagTooltip && getTagTooltip(value);

    if (loading) return <span />;

    return (
      <Tag closable={closable} onClose={onClose}>
        <AntTooltip title={tooltip} hide={!tooltip}>
          {label}
        </AntTooltip>
      </Tag>
    );
  };

  const fieldContent = ({ field, form }: FieldProps) => {
    const { errors, touched, setFieldValue, setFieldTouched } = form;
    const showValidationInfo = !!(_get(errors, name) && _get(touched, name));
    const optionValues = field.value.length > 0 ? field.value : [[]];

    return (
      <Form.Item
        className={className}
        label={label}
        required={required}
        hasFeedback
        validateStatus={showValidationInfo ? 'error' : ''}
        help={(showValidationInfo && (_get(errors, name) as string)) || undefined}
      >
        {optionValues.map((value: number[], i: number) => (
          <div key={`${name}-${i}`} className="flex items-center">
            <Select
              className={i > 0 ? 'mt-3' : ''}
              mode="multiple"
              placeholder={placeholder}
              value={value || []}
              onChange={(value: number[]) => {
                const newValue = optionValues.map((v: number[], index: number) =>
                  index === i ? value : v
                );
                setFieldValue(name, newValue);
                setFieldTouched(name);
              }}
              disabled={disabled}
              loading={loading}
              optionFilterProp="label"
              tagRender={showTagTooltip ? tagRender : undefined}
              options={valueList}
              {...selectProps}
            />
            <Button
              className={i > 0 ? 'mt-3' : ''}
              type="link"
              onClick={() => {
                const newValue = optionValues.filter((_: any, index: number) => index !== i);
                setFieldValue(name, newValue);
                setFieldTouched(name);
              }}
              disabled={disabled || optionValues.length <= 1}
              size="small"
            >
              <DeleteOutlined />
            </Button>
          </div>
        ))}
        <Button
          type="link"
          className="mt-3"
          onClick={() => {
            setFieldValue(name, [
              ...optionValues,
              [], // Add a new empty array for the new row
            ]);
          }}
          disabled={disabled || optionValues[optionValues.length - 1].length === 0}
          size="small"
        >
          {t('common:addRow')}
        </Button>
      </Form.Item>
    );
  };

  return fastField ? (
    <FastField name={name}>{fieldContent}</FastField>
  ) : (
    <Field name={name}>{fieldContent}</Field>
  );
};

export default FormMultiMultiSelect;
